
import * as React from "react"
import { cn } from "@/lib/utils"

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  maxLength?: number;
  showCounter?: boolean;
}

export interface AutoExpandTextareaProps extends TextareaProps {
  autoResize?: boolean;
  minRows?: number;
  maxRows?: number;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, maxLength, showCounter = false, value, onChange, ...props }, ref) => {
    const currentLength = typeof value === 'string' ? value.length : 0;
    const percentage = maxLength ? (currentLength / maxLength) * 100 : 0;
    
    const getCounterColor = () => {
      if (percentage >= 95) return 'text-red-500';
      if (percentage >= 80) return 'text-yellow-500';
      return 'text-gray-500';
    };

    return (
      <div className="relative">
        <textarea
          className={cn(
            "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none overflow-y-auto",
            "dark:bg-card dark:border-border dark:text-foreground dark:placeholder:text-muted-foreground dark:focus-visible:ring-ring",
            maxLength && currentLength > maxLength && "border-red-500 focus-visible:ring-red-500",
            className
          )}
          ref={ref}
          value={value}
          onChange={onChange}
          maxLength={maxLength}
          {...props}
        />
        {showCounter && maxLength && (
          <div className={cn(
            "absolute bottom-2 right-2 text-xs font-medium",
            getCounterColor()
          )}>
            {currentLength}/{maxLength}
          </div>
        )}
      </div>
    )
  }
)

const AutoExpandTextarea = React.forwardRef<HTMLTextAreaElement, AutoExpandTextareaProps>(
  ({ className, autoResize = true, minRows = 1, maxRows = 9, value, onChange, ...props }, ref) => {
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    const combinedRef = React.useMemo(() => {
      return (node: HTMLTextAreaElement) => {
        textareaRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      };
    }, [ref]);

    const adjustHeight = React.useCallback(() => {
      const textarea = textareaRef.current;
      if (!textarea || !autoResize) return;

      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      
      // Calculate line height and set constraints
      const lineHeight = parseInt(getComputedStyle(textarea).lineHeight) || 20;
      const minHeight = lineHeight * minRows;
      const maxHeight = lineHeight * maxRows;
      
      // Set height based on content but within constraints
      const scrollHeight = textarea.scrollHeight;
      const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);
      
      textarea.style.height = `${newHeight}px`;
      textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
    }, [autoResize, minRows, maxRows]);

    React.useEffect(() => {
      adjustHeight();
    }, [value, adjustHeight]);

    return (
      <textarea
        ref={combinedRef}
        className={cn(
          "flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none transition-all duration-200",
          "dark:bg-card dark:border-border dark:text-foreground dark:placeholder:text-muted-foreground dark:focus-visible:ring-ring",
          className
        )}
        value={value}
        onChange={(e) => {
          onChange?.(e);
          // Trigger height adjustment after the value updates
          setTimeout(adjustHeight, 0);
        }}
        style={{
          minHeight: `${(parseInt(getComputedStyle(document.documentElement).fontSize) || 16) * 1.25 * minRows}px`
        }}
        {...props}
      />
    );
  }
)
Textarea.displayName = "Textarea"

AutoExpandTextarea.displayName = "AutoExpandTextarea"

export { Textarea, AutoExpandTextarea }
